import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { useEvent } from "shared/lib/useEvent"
import { InstallationReference } from "shared/types/InstallationReference"
import { Button } from "shared/ui/buttons/Button"
import { ChipVariant } from "shared/ui/chip/Chip"
import { ImageOverlayPayload } from "shared/ui/image-overlay/ImageOverlay"
import { Image } from "shared/ui/image/Image"
import { Toggle } from "shared/ui/input/boolean/toggle/Toggle"
import { Tag, Tags } from "shared/ui/tags/Tags"

import "../../styles/_installation-reference-selection.scss"

interface InstallationReferenceCardProps {
  installationReference: InstallationReference
  isSelected: boolean
  onSelect: (id: string) => void
  onZoomImage: (payload: ImageOverlayPayload) => void
}

export const InstallationReferenceCard = React.memo(
  ({
    installationReference,
    isSelected,
    onSelect,
    onZoomImage
  }: InstallationReferenceCardProps) => {
    const { t } = useTranslation()
    const handleSelect = useEvent(() => onSelect(installationReference.id))
    const handleZoomRoomImage = useEvent(() =>
      onZoomImage({
        title: `${installationReference.title} - ${t(
          "InstallationReference.RoomImage"
        )}`,
        content: installationReference.roomImage
      })
    )

    const handleZoomDetailImage = useEvent(() =>
      onZoomImage({
        title: `${installationReference.title} - ${t(
          "InstallationReference.DetailImage"
        )}`,
        content: installationReference.detailImage
      })
    )

    const tags = useMemo(() => {
      const result: Tag[] = []
      if (installationReference.variantKey) {
        result.push({
          key: "variant-key",
          title: installationReference.variantKey
        })
      }
      result.push(
        ...installationReference.buildingType.map(buildingType => ({
          key: buildingType,
          title: t(`BuildingReference.BuildingType.${buildingType}`)
        }))
      )
      result.push(
        ...installationReference.roomType.map(roomType => ({
          key: roomType,
          title: t(`RoomType.${roomType}`)
        }))
      )
      return result
    }, [installationReference])

    return (
      <div
        className={classNames(
          "card",
          "card--elevated",
          {
            "card--selected": isSelected
          },
          "installation-reference-card"
        )}
        data-testid="installation-reference-card"
      >
        <Toggle.Root
          className="installation-reference-card__header"
          onChange={handleSelect}
        >
          <Toggle.LabelContainer>
            <Toggle.Label className="installation-reference-card__title">
              {installationReference.title}
            </Toggle.Label>
          </Toggle.LabelContainer>
          <Toggle.Input checked={isSelected} />
        </Toggle.Root>

        <div className="installation-reference-card__images">
          <Button
            className="installation-reference-card__room-image"
            style={{ cursor: "zoom-in" }}
            onClick={handleZoomRoomImage}
          >
            <Image>{installationReference.roomImage}</Image>
          </Button>
          <Button
            className="installation-reference-card__detail-image"
            style={{ cursor: "zoom-in" }}
            onClick={handleZoomDetailImage}
          >
            <Image>{installationReference.detailImage}</Image>
          </Button>
        </div>
        <div className="installation-reference-card__content">
          <Tags
            className="installation-reference-card__tags"
            chipStyle={ChipVariant.INFO}
            tags={tags}
          />
        </div>
      </div>
    )
  }
)
